import React from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom-v5-compat";

import {
  Button,
  Price,
  PrimaryAction,
  useSubmitButton,
  LinkButton,
} from "../../Atoms";
import { useVenue } from "../../VenueContext";
import { getVenueHasTablePayOrdering } from "../../venueSelectors";
import { TabDetails } from "../Cart/CartDetailsForm";
import { CartItems } from "../Cart/CartItems";
import { OrderedOrderItems } from "../Cart/OrderedOrderItem";
import { SoldOutItemsError } from "../Cart/errors";
import { finalizeCartOrder, createNextCart } from "../Cart/requests";
import { getCartTotal } from "../Cart/selectors";
import { fetchCustomerTab } from "../CustomerTab/requests";
import { CustomerTab } from "../CustomerTab/schema";
import { useSession, useSessionDispatch } from "../Session/SessionContext";

export const PendingCartOnTab = ({
  customerTab,
}: {
  customerTab: CustomerTab;
}) => {
  const { t } = useTranslation();
  const session = useSession();
  const dispatch = useSessionDispatch();
  const cart = session.cart;
  const venue = useVenue();
  const navigate = useNavigate();
  const tablePayOrdering = getVenueHasTablePayOrdering(venue);
  const [soldOutItemsUuids, setSoldOutItemsUuids] = React.useState<string[]>(
    [],
  );
  const hasSoldOutItems = soldOutItemsUuids.length > 0;

  const [submissionState, trackSubmit] = useSubmitButton();
  const cartTotal = getCartTotal(session.cart);

  const placeCartOrderOnTab = async () => {
    try {
      const customerTab = session.customerTab;
      if (!customerTab) {
        console.error("Can't place order on unsaved customer tab");
        return;
      }
      const cartResponse = await finalizeCartOrder(cart.uuid);
      const newCustomerTab = await fetchCustomerTab({ uuid: customerTab.uuid });
      const newCart = await createNextCart({
        customerTab,
        lastCart: cart,
        venue,
      });

      dispatch({ action: "updateCart", payload: newCart });
      dispatch({ action: "updateCustomerTab", payload: newCustomerTab });
      navigate(`/order/${cartResponse.orderId}/ordered`);
    } catch (e) {
      if (e instanceof SoldOutItemsError) {
        console.log(e);
        setSoldOutItemsUuids(e.soldOutItemsUuids);
        return;
      }
      console.error(e);
    }
  };

  if (!tablePayOrdering) {
    throw new Error("Table pay ordering is not enabled");
  }
  const checkoutButton = !hasSoldOutItems ? (
    <Button
      submissionState={submissionState}
      type="button"
      onClick={trackSubmit(placeCartOrderOnTab)}
      primary
    >
      {t("cart.placeOrder")}{" "}
      <Price value={cartTotal} showCurrency showDecimals />
    </Button>
  ) : (
    <div className="bg-venue-secondary text-center text-venue-highlight">
      {t("cart.orderingSoldOutItems")}{" "}
    </div>
  );

  return (
    <div>
      <div className="space-y-32">
        <div className="flex-1 space-y-32">
          {tablePayOrdering && <CartItems />}
          {tablePayOrdering && (
            <div className="">
              <LinkButton secondary to="/">
                {t("cart.addMoreItems")}
              </LinkButton>
            </div>
          )}
          <TabDetails customerTab={customerTab} />
          <div className="flex items-center space-x-32">
            <div className="flex-none">
              <div className="text-2xl font-extrabold">
                <Price value={cartTotal} />{" "}
              </div>
            </div>
            {tablePayOrdering && (
              <div className="hidden flex-1 md:block">{checkoutButton}</div>
            )}
          </div>
          <OrderedOrderItems />
        </div>
      </div>
      {tablePayOrdering && <PrimaryAction>{checkoutButton}</PrimaryAction>}
    </div>
  );
};
